import React, { useState } from "react";
import { ExclamationCircleFill, Microsoft } from "react-bootstrap-icons";
import { cn } from "../lib/cn";

type Props = {
  onLoginSuccess: (token: string, username: string, role: string) => void;
  /** True when the user was signed out by their session lapsing, not by choice. */
  sessionLapsed?: boolean;
};

export default function Login({ onLoginSuccess, sessionLapsed = false }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || "Login failed");
        return;
      }
      onLoginSuccess(data.token, data.username, data.role);
    } catch {
      setError("Unable to connect to the backend.");
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = cn(
    "border-line bg-surface-sunken text-ink placeholder:text-ink-subtle",
    "h-11 w-full rounded-lg border px-3.5 text-sm",
    "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
    "focus:border-accent focus:bg-surface focus:outline-none",
  );

  return (
    <div className="bg-canvas flex h-full items-center justify-center overflow-y-auto p-6">
      <div className="animate-rise-in w-full max-w-sm">
        <div className="mb-7 text-center">
          <img
            src="/logo.png"
            alt="SCHEmunda"
            className="mx-auto mb-5 h-auto w-32"
          />
          <h1 className="text-ink text-xl font-semibold tracking-tight">
            Sign in to SCHEmunda
          </h1>
          <p className="text-ink-muted mt-1.5 text-sm">
            Collaborative BPMN process editing
          </p>
        </div>

        <div className="bg-surface border-line shadow-panel rounded-2xl border p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="login-username"
                className="text-ink-muted mb-1.5 block text-xs font-semibold tracking-wide uppercase"
              >
                Username
              </label>
              <input
                id="login-username"
                type="text"
                autoComplete="username"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className={fieldClass}
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="text-ink-muted mb-1.5 block text-xs font-semibold tracking-wide uppercase"
              >
                Password
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={fieldClass}
              />
            </div>

            {/* Shown instead of nothing when a session lapses mid-use: the
                sign-in screen appearing unannounced reads as a bug. Hidden
                once there is a real error, so the two never stack. */}
            {sessionLapsed && !error && (
              <div
                role="status"
                className="bg-warning-soft text-warning animate-fade-in flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm font-medium"
              >
                <ExclamationCircleFill
                  size={14}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0"
                />
                Your session expired. Please sign in again — any unsaved work in
                another tab is still there.
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="bg-danger-soft text-danger animate-fade-in flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm font-medium"
              >
                <ExclamationCircleFill
                  size={14}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0"
                />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={cn(
                "bg-brand hover:bg-brand-hover flex h-11 w-full items-center justify-center gap-2",
                "rounded-lg text-sm font-semibold text-white",
                "transition-[background-color,transform,opacity] duration-(--duration-instant) ease-(--ease-standard)",
                "active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60",
              )}
            >
              {loading && (
                <span
                  aria-hidden="true"
                  className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white"
                />
              )}
              {loading ? "Signing in" : "Sign in"}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <span className="bg-line h-px flex-1" />
            <span className="text-ink-subtle text-[11px] font-semibold tracking-wider uppercase">
              or
            </span>
            <span className="bg-line h-px flex-1" />
          </div>

          <a
            href={`${API_URL}/api/auth/microsoft`}
            className={cn(
              "bg-microsoft hover:bg-microsoft-hover flex h-11 w-full items-center justify-center gap-2.5",
              "rounded-lg text-sm font-semibold text-white no-underline",
              "transition-[background-color,transform] duration-(--duration-instant) ease-(--ease-standard)",
              "active:scale-[0.99]",
            )}
          >
            <Microsoft size={15} aria-hidden="true" />
            Sign in with Microsoft
          </a>
        </div>
      </div>
    </div>
  );
}
